/**
 * The API route contract.
 *
 * `ApiContract` types every endpoint's params, query, request body and response
 * body. `API_ROUTES` gives the method and path for each. The two are keyed by
 * the same union, so a route added to one and not the other fails the build.
 *
 * Route handlers must import these types rather than restate them.
 */
/** Base paths. The admin base sits behind Cloudflare Access. */
export const API_BASE = {
    PORTAL: '/api/portal',
    ADMIN: '/api/admin',
};
export const API_ROUTES = {
    'portal.requestOtp': {
        method: 'POST',
        path: '/api/portal/:slug/auth/request-otp',
        auth: 'public',
    },
    'portal.verifyOtp': {
        method: 'POST',
        path: '/api/portal/:slug/auth/verify-otp',
        auth: 'public',
    },
    'portal.listDocuments': {
        method: 'GET',
        path: '/api/portal/:slug/documents',
        auth: 'session',
    },
    'portal.acceptNda': {
        method: 'POST',
        path: '/api/portal/:slug/nda/accept',
        auth: 'session',
    },
    'portal.createDocumentUrl': {
        method: 'POST',
        path: '/api/portal/:slug/documents/:documentId/url',
        auth: 'session',
    },
    'portal.getDocumentContent': {
        method: 'GET',
        path: '/api/portal/:slug/documents/:documentId/content',
        auth: 'session',
    },
    'portal.listAccess': {
        method: 'GET',
        path: '/api/portal/:slug/access',
        auth: 'session',
    },
    'portal.revealCredential': {
        method: 'POST',
        path: '/api/portal/:slug/credentials/:credentialId/reveal',
        auth: 'session',
    },
    'admin.listCustomers': {
        method: 'GET',
        path: '/api/admin/customers',
        auth: 'cloudflare-access',
    },
    'admin.getCustomer': {
        method: 'GET',
        path: '/api/admin/customers/:id',
        auth: 'cloudflare-access',
    },
    'admin.createCustomer': {
        method: 'POST',
        path: '/api/admin/customers',
        auth: 'cloudflare-access',
    },
    'admin.updateCustomer': {
        method: 'PATCH',
        path: '/api/admin/customers/:id',
        auth: 'cloudflare-access',
    },
    'admin.deleteCustomer': {
        method: 'DELETE',
        path: '/api/admin/customers/:id',
        auth: 'cloudflare-access',
    },
    'admin.listUsers': {
        method: 'GET',
        path: '/api/admin/customers/:customerId/users',
        auth: 'cloudflare-access',
    },
    'admin.createUser': {
        method: 'POST',
        path: '/api/admin/customers/:customerId/users',
        auth: 'cloudflare-access',
    },
    'admin.deleteUser': {
        method: 'DELETE',
        path: '/api/admin/users/:id',
        auth: 'cloudflare-access',
    },
    'admin.listDocuments': {
        method: 'GET',
        path: '/api/admin/documents',
        auth: 'cloudflare-access',
    },
    'admin.getDocument': {
        method: 'GET',
        path: '/api/admin/documents/:id',
        auth: 'cloudflare-access',
    },
    'admin.createDocument': {
        method: 'POST',
        path: '/api/admin/documents',
        auth: 'cloudflare-access',
    },
    'admin.uploadDocument': {
        method: 'POST',
        path: '/api/admin/documents/upload',
        auth: 'cloudflare-access',
    },
    'admin.listArchivedDocuments': {
        method: 'GET',
        path: '/api/admin/documents/archived',
        auth: 'cloudflare-access',
    },
    'admin.updateDocument': {
        method: 'PATCH',
        path: '/api/admin/documents/:id',
        auth: 'cloudflare-access',
    },
    'admin.deleteDocument': {
        method: 'DELETE',
        path: '/api/admin/documents/:id',
        auth: 'cloudflare-access',
    },
    'admin.listCustomerDocuments': {
        method: 'GET',
        path: '/api/admin/customers/:customerId/documents',
        auth: 'cloudflare-access',
    },
    'admin.setCustomerDocuments': {
        method: 'PUT',
        path: '/api/admin/customers/:customerId/documents',
        auth: 'cloudflare-access',
    },
    'admin.unmapCustomerDocument': {
        method: 'DELETE',
        path: '/api/admin/customers/:customerId/documents/:documentId',
        auth: 'cloudflare-access',
    },
    'admin.listRequestItems': {
        method: 'GET',
        path: '/api/admin/customers/:customerId/request-items',
        auth: 'cloudflare-access',
    },
    'admin.createRequestItem': {
        method: 'POST',
        path: '/api/admin/customers/:customerId/request-items',
        auth: 'cloudflare-access',
    },
    'admin.updateRequestItem': {
        method: 'PATCH',
        path: '/api/admin/request-items/:id',
        auth: 'cloudflare-access',
    },
    'admin.deleteRequestItem': {
        method: 'DELETE',
        path: '/api/admin/request-items/:id',
        auth: 'cloudflare-access',
    },
    'admin.listCredentials': {
        method: 'GET',
        path: '/api/admin/customers/:customerId/credentials',
        auth: 'cloudflare-access',
    },
    'admin.createCredential': {
        method: 'POST',
        path: '/api/admin/customers/:customerId/credentials',
        auth: 'cloudflare-access',
    },
    'admin.revokeCredential': {
        method: 'POST',
        path: '/api/admin/credentials/:id/revoke',
        auth: 'cloudflare-access',
    },
    'admin.listCustomerLinks': {
        method: 'GET',
        path: '/api/admin/customers/:customerId/links',
        auth: 'cloudflare-access',
    },
    'admin.createCustomerLink': {
        method: 'POST',
        path: '/api/admin/customers/:customerId/links',
        auth: 'cloudflare-access',
    },
    'admin.reorderCustomerLinks': {
        method: 'PUT',
        path: '/api/admin/customers/:customerId/links/order',
        auth: 'cloudflare-access',
    },
    'admin.deleteCustomerLink': {
        method: 'DELETE',
        path: '/api/admin/links/:id',
        auth: 'cloudflare-access',
    },
    'admin.listAuditLog': {
        method: 'GET',
        path: '/api/admin/audit-log',
        auth: 'cloudflare-access',
    },
};
//# sourceMappingURL=api.js.map